/** XeAgents.ai - Safe date formatting helpers **/
import { format, formatDistanceToNow, isValid } from "date-fns";

export type DateInput = string | number | Date | null | undefined;

/**
 * Parse an API value into a Date, or null when it is missing or unparseable.
 * Use this when the caller needs to branch on validity rather than render text.
 */
export function parseDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : null;
}

/**
 * date-fns format() that renders a fallback instead of throwing.
 * Raw format() throws RangeError on an invalid Date, which blanks the page
 * when it happens during render.
 */
export function formatDate(value: DateInput, pattern: string, fallback = "—"): string {
  const date = parseDate(value);
  if (!date) return fallback;
  try {
    return format(date, pattern);
  } catch {
    return fallback;
  }
}

/**
 * ISO-8601 string for structured data, filenames and API params.
 * Date.toISOString() throws RangeError on an invalid Date.
 */
export function toIsoString(value: DateInput, fallback = ""): string {
  const date = parseDate(value);
  return date ? date.toISOString() : fallback;
}

/** date-fns formatDistanceToNow() that renders a fallback instead of throwing. */
export function formatRelativeTime(value: DateInput, fallback = "—"): string {
  const date = parseDate(value);
  if (!date) return fallback;
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return fallback;
  }
}
